/** @jsxImportSource @emotion/react */
import { useMemo } from 'react'
import { prefillDynamicFormAnswers } from 'src/utils/prefillDynamicFormAnswers'
import DynamicFormReadOnly from 'src/components/DynamicForm/DynamicFormReadOnly'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { UsersMeRegistrationResponse } from '@sportninja/common/types/usersMeRegistration'

interface RegistrationDetailsDynamicStandaloneProps {
  scheduleId: string
  registrationUid: string
  registrationDetails: UsersMeRegistrationResponse | null
  loading: boolean
  error: Error | null
}

export const getSelectedForm = (
  registration: any,
  formType: string
): any | null => {
  if (!registration) {
    return null
  }
  const form =
    formType === 'team' ? registration.team_form : registration.player_form

  return form || null
}

/**
 * RegistrationDetailsDynamicStandalone: Read-only display of dynamic form data
 * for the standalone registration details page.
 *
 * Renders form sections and field values as label/value pairs instead of
 * disabled form inputs for a cleaner read-only experience.
 */
const RegistrationDetailsDynamicStandalone = ({
  registrationDetails,
  loading,
  error,
}: RegistrationDetailsDynamicStandaloneProps) => {
  const isTeamRegistration = useMemo(
    () => registrationDetails?.registration_type === 'Team',
    [registrationDetails?.registration_type]
  )

  const selectedForm = useMemo(() => {
    if (!registrationDetails) {
      return null
    }
    const formType = isTeamRegistration ? 'team' : 'player'
    return getSelectedForm(registrationDetails, formType)
  }, [registrationDetails, isTeamRegistration])

  // Build answers from saved form data for display
  const answers = useMemo(() => {
    if (!selectedForm || !registrationDetails?.form) {
      return {}
    }

    const convertedForm: {
      [sectionKey: string]: Array<{ id: string; name: string; value: string }>
    } = {}
    Object.entries(registrationDetails.form).forEach(([sectionKey, fields]) => {
      convertedForm[sectionKey] = fields
        .filter((field) => field.value !== null)
        .map((field) => ({
          id: field.id,
          name: field.name,
          value: field.value as string,
        }))
    })

    return prefillDynamicFormAnswers(selectedForm, convertedForm)
  }, [selectedForm, registrationDetails?.form])

  if (loading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 50vh;
        `}
      >
        Loading form...
      </div>
    )
  }

  if (error) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 50vh;
          color: ${colors.FIERY_RED};
        `}
      >
        Error loading form: {error?.message || 'Unknown error'}
      </div>
    )
  }

  if (!selectedForm) {
    return null
  }

  return <DynamicFormReadOnly groups={selectedForm.groups} answers={answers} />
}

export default RegistrationDetailsDynamicStandalone
